import findPeople from './findPeople';
import findPeopleInSparseFolder from './findPeopleInSparseFolder';
import type { HubPersona } from '../../../shared';

export type TrackedRequestResponse = {
    offset?: number;
    totalCount?: number;
    personas: HubPersona[];
    getIsCurrentRequest: () => boolean;
};

// Keep track of which requests are currently outgoing
// If we have a pending request with identical parameters, it is redundant to trigger further requests
const requestParametersToKey = (
    folderId: string,
    offset: number,
    limit: number,
    sortingPath: string
) => JSON.stringify({ folderId, offset, limit, sortingPath });

const outgoingRequestsMap: {
    [parameterKey: string]: Promise<TrackedRequestResponse>;
} = {};
let lastRequest: Promise<TrackedRequestResponse>;

// Sets an n-tuple in the map to track an outgoing request
const setOutgoingRequest = (
    folderId: string,
    offset: number,
    limit: number,
    sortingPath: string,
    promise: Promise<TrackedRequestResponse>
) => {
    return (outgoingRequestsMap[
        requestParametersToKey(folderId, offset, limit, sortingPath)
    ] = promise);
};

// Retrieves any existing outgoing requests for the given parameters
const getOutgoingRequest = (
    folderId: string,
    offset: number,
    limit: number,
    sortingPath: string
) => {
    return outgoingRequestsMap[requestParametersToKey(folderId, offset, limit, sortingPath)];
};

// Removes the request n-tuple from the map
const freeOutgoingRequest = (
    folderId: string,
    offset: number,
    limit: number,
    sortingPath: string
) => delete outgoingRequestsMap[requestParametersToKey(folderId, offset, limit, sortingPath)];

export const findPeopleWithRequestTracking = (
    folderIdToFetch: string,
    currentOffset: number,
    limit: number,
    sortingPath: string,
    isExemptFromCurrentRequestCheck: boolean,
    isSparseFolder: boolean
): Promise<TrackedRequestResponse> => {
    const existingRequest = getOutgoingRequest(folderIdToFetch, currentOffset, limit, sortingPath);

    if (existingRequest) {
        return existingRequest;
    }

    const thisRequest = setOutgoingRequest(
        folderIdToFetch,
        currentOffset,
        limit,
        sortingPath,
        isSparseFolder
            ? findPeopleInSparseFolder(folderIdToFetch, currentOffset, limit)
                  .then(result => {
                      freeOutgoingRequest(folderIdToFetch, currentOffset, limit, sortingPath);
                      return {
                          offset: result.offset,
                          totalCount: result.totalCount,
                          personas: result.personas,
                          getIsCurrentRequest: () => lastRequest === thisRequest,
                      };
                  })
                  .catch(error => {
                      freeOutgoingRequest(folderIdToFetch, currentOffset, limit, sortingPath);
                      throw error;
                  })
            : findPeople(folderIdToFetch, currentOffset, limit, sortingPath)
                  .then(personas => {
                      freeOutgoingRequest(folderIdToFetch, currentOffset, limit, sortingPath);
                      return {
                          personas,
                          getIsCurrentRequest: () => lastRequest === thisRequest,
                      };
                  })
                  .catch(error => {
                      freeOutgoingRequest(folderIdToFetch, currentOffset, limit, sortingPath);
                      throw error;
                  })
    );

    if (!isExemptFromCurrentRequestCheck) {
        lastRequest = thisRequest;
    }

    return thisRequest;
};
